import type { BookmarkData, BookmarkItem } from '@/types'
// API_BASE_URL
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api"

// 将扁平的书签数组转换为嵌套的树形结构
function buildBookmarkTree(flatBookmarks: BookmarkItem[]): BookmarkItem[] {
  // 创建 ID 到节点的映射
  const itemMap = new Map<string, BookmarkItem>()

  // 第一遍：创建所有节点并放入映射
  flatBookmarks.forEach(item => {
    itemMap.set(item.id, { ...item, children: [] })
  })

  const roots: BookmarkItem[] = []

  // 第二遍：构建树形结构
  flatBookmarks.forEach(item => {
    const node = itemMap.get(item.id)!
    const parentId = item.parentId

    // 根节点（parentId 为 '0' 或 '1'）
    if (parentId === '0' || parentId === '1') {
      roots.push(node)
    } else {
      // 添加到父节点的 children
      const parent = itemMap.get(parentId)
      if (parent) {
        if (!parent.children) {
          parent.children = []
        }
        parent.children.push(node)
      }
    }
  })

  // 按 index 排序
  const sortIndex = (items: BookmarkItem[]) => {
    items.sort((a, b) => a.index - b.index)
    items.forEach(item => {
      if (item.children && item.children.length > 0) {
        sortIndex(item.children)
      }
    })
  }

  sortIndex(roots)

  return roots
}

const STORAGE_KEY = 'bookmark_data'
const STORAGE_TIMESTAMP_KEY = 'bookmark_timestamp'

// 从 localStorage 读取缓存数据
function getCachedData(): BookmarkData | null {
  try {
    const cachedData = localStorage.getItem(STORAGE_KEY)
    const cachedTimestamp = localStorage.getItem(STORAGE_TIMESTAMP_KEY)
    if (cachedData && cachedTimestamp) {
      return {
        ...JSON.parse(cachedData),
        timestamp: parseInt(cachedTimestamp)
      }
    }
  } catch (error) {
    console.warn('读取缓存失败:', error)
  }
  return null
}

// 保存数据到 localStorage
function saveToCache(data: BookmarkData): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      bookmarks: data.bookmarks,
      settings: data.settings,
      stats: data.stats
    }))
    localStorage.setItem(STORAGE_TIMESTAMP_KEY, (data.timestamp || Date.now()).toString())
  } catch (error) {
    console.warn('保存缓存失败:', error)
  }
}

// 比较两个书签数据是否有变化
function hasDataChanged(localData: BookmarkData, remoteData: BookmarkData): boolean {
  const localStr = JSON.stringify(localData.bookmarks)
  const remoteStr = JSON.stringify(remoteData.bookmarks)
  return localStr !== remoteStr
}

export async function fetchBookmarkData(): Promise<BookmarkData> {
  const cachedData = getCachedData()

  try {
    const response = await fetch(`${API_BASE_URL}/bookmark/data`)

    // 新版：以 HTTP 状态码为准，错误体为 { jsonapi, errors: [...] }
    if (!response.ok) {
      const errDoc = await response.json().catch(() => null)
      const err = errDoc?.errors?.[0]
      throw new Error(err?.detail || err?.code || `HTTP error! status: ${response.status}`)
    }

    const doc = await response.json()

    // 改造后：响应被包装为 { jsonapi, data, meta }
    const resource = doc?.data

    // 后端无数据时 data 为 null
    if (resource === null || resource === undefined) {
      throw new Error('后端暂无书签数据')
    }

    // 业务字段在 data.attributes，资源 id 在 data.id（不在 attributes 内）
    const data = resource.attributes ?? resource

    // 将扁平数据转换为嵌套结构
    const nestedBookmarks = buildBookmarkTree(data.bookmarks || [])

    const remoteData: BookmarkData = {
      bookmarks: nestedBookmarks,
      settings: data.settings || {},
      stats: data.stats || {
        totalBookmarks: countBookmarks(nestedBookmarks),
        totalFolders: countFolders(nestedBookmarks)
      },
      timestamp: normalizeTimestamp(data.timestamp)
    }

    // 检查数据是否有变化
    if (cachedData && !hasDataChanged(cachedData, remoteData)) {
      console.log('数据未变化，使用缓存')
      return cachedData
    }

    // 数据有变化，更新缓存
    saveToCache(remoteData)
    console.log('数据已更新')
    return remoteData
  } catch (error) {
    console.warn('API 加载失败，使用缓存数据:', error)
    if (cachedData) {
      return cachedData
    }
    console.warn('缓存不存在，使用默认数据')
    return {
      bookmarks: [],
      settings: {},
      stats: {
        totalBookmarks: 0,
        totalFolders: 0
      },
      timestamp: Date.now()
    }
  }
}

// 将时间戳规范化为 number（兼容 ISO 字符串与数字两种格式）
function normalizeTimestamp(ts: string | number | null | undefined): number {
  if (ts === null || ts === undefined || ts === '') {
    return Date.now()
  }
  const parsed = typeof ts === 'string' ? Date.parse(ts) : ts
  return Number.isFinite(parsed) ? parsed : Date.now()
}

function countBookmarks(items: BookmarkItem[]): number {
  let count = 0
  items.forEach(item => {
    if (item.isFolder && item.children) {
      count += countBookmarks(item.children)
    } else {
      count++
    }
  })
  return count
}

function countFolders(items: BookmarkItem[]): number {
  let count = 0
  items.forEach(item => {
    if (item.isFolder) {
      count++
      if (item.children) {
        count += countFolders(item.children)
      }
    }
  })
  return count
}

export function getFavicon(url: string): string {
  try {
    const urlObj = new URL(url)
    return `https://ico.la4.cn/ico.php?url=${urlObj.hostname}`
  } catch {
    return ''
  }
}
